import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  alpha,
  useTheme,
  Avatar,
  Badge,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Star as StarIcon,
  AccessTime as AccessTimeIcon,
  Place as PlaceIcon,
  Person as PersonIcon
} from '@mui/icons-material';

// Componente para exibir uma senha sendo atendida
const SenhaEmAtendimento = ({ senha }) => {
  const theme = useTheme();

  // Calcular tempo de atendimento
  const calcularTempoAtendimento = () => {
    const dataInicio = new Date(senha.atendimento_iniciado_em || senha.chamada_em);
    const agora = new Date();
    const diferencaMs = agora - dataInicio;
    const minutos = Math.floor(diferencaMs / (1000 * 60));
    
    if (minutos < 60) {
      return `${minutos} min`;
    } else {
      const horas = Math.floor(minutos / 60);
      const minutosRestantes = minutos % 60;
      return `${horas}h ${minutosRestantes}min`;
    }
  };

  // Função para obter cor de acordo com o tempo de atendimento
  const getCorTempoAtendimento = () => {
    const dataInicio = new Date(senha.atendimento_iniciado_em || senha.chamada_em);
    const agora = new Date();
    const diferencaMs = agora - dataInicio;
    const minutos = Math.floor(diferencaMs / (1000 * 60));
    
    if (minutos < 5) return theme.palette.success.main;
    if (minutos < 15) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Decidir a cor de fundo do cartão baseada no tempo de atendimento
  const getBackgroundColor = () => {
    const dataInicio = new Date(senha.atendimento_iniciado_em || senha.chamada_em);
    const agora = new Date();
    const diferencaMs = agora - dataInicio;
    const minutos = Math.floor(diferencaMs / (1000 * 60));
    
    if (minutos < 5) return alpha(theme.palette.success.main, 0.05);
    if (minutos < 15) return alpha(theme.palette.warning.main, 0.05);
    return alpha(theme.palette.error.main, 0.05);
  };

  const nomeAtendente = senha.atendente?.nome || (senha.usuario?.nome || 'Atendente');
  const inicialAtendente = nomeAtendente.charAt(0).toUpperCase();

  return (
    <Card 
      sx={{
        mb: 2,
        borderLeft: `5px solid ${getCorTempoAtendimento()}`,
        transition: 'all 0.2s ease',
        bgcolor: getBackgroundColor(),
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', p: 2, '&:last-child': { pb: 2 } }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mr: 1 }}>
              {senha.senha || senha.numero}
            </Typography>
            {senha.preferencial && (
              <Chip 
                icon={<StarIcon fontSize="small" />} 
                label="Preferencial" 
                size="small" 
                color="warning"
                sx={{ mr: 1 }}
              />
            )}
            <Chip 
              label={senha.servico_nome || senha.tipo} 
              size="small" 
              color="primary"
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">
              Em atendimento: {calcularTempoAtendimento()}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
          <Chip 
            icon={<PlaceIcon fontSize="small" />}
            label={`Guichê ${senha.guiche}`}
            size="small"
            color="success"
            sx={{ fontWeight: 'bold', mb: 1 }}
          />
          
          <Tooltip title={nomeAtendente} arrow placement="top">
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: alpha(theme.palette.background.paper, 0.5), px: 1.5, py: 0.5, borderRadius: 10 }}>
              <Avatar 
                sx={{ 
                  width: 28, 
                  height: 28, 
                  fontSize: '0.8rem',
                  mr: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.8)
                }}
              >
                {inicialAtendente}
              </Avatar>
              <Typography variant="body2" fontWeight={500}>
                {nomeAtendente}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

// Componente principal de senhas em atendimento
const SenhasAtendimentoList = ({ senhas }) => {
  const theme = useTheme();
  
  // Se não houver senhas em atendimento, exibe mensagem
  if (!senhas || senhas.length === 0) {
    return (
      <Paper elevation={0} sx={{ 
        py: 8, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        bgcolor: alpha(theme.palette.info.main, 0.05),
        borderRadius: 2,
        height: '100%'
      }}>
        <PersonIcon sx={{ fontSize: 48, color: alpha(theme.palette.info.main, 0.5), mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Não há senhas em atendimento no momento.
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Box sx={{ 
      height: '100%',
      overflow: 'auto',
      pr: 1,
      mt: 1,
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: alpha(theme.palette.text.primary, 0.05),
        borderRadius: '10px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: alpha(theme.palette.primary.main, 0.2),
        borderRadius: '10px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: alpha(theme.palette.primary.main, 0.3),
      }
    }}>
      {senhas.map((senha) => (
        <SenhaEmAtendimento key={senha.id} senha={senha} />
      ))}
    </Box>
  );
};

export default SenhasAtendimentoList; 